import React, { useState, useRef, useEffect } from 'react';
import { Download, Loader2, ChevronDown } from 'lucide-react';
import { useExport } from '../hooks/useExport';
import clsx from 'clsx';

/**
 * ExportMenu — dropdown button with PDF and/or Excel export options.
 *
 * Props:
 *   exports: [{ label, apiFn, filename }]
 *
 * Usage:
 *   <ExportMenu exports={[
 *     { label: 'PDF',   apiFn: exportAPI.assetsPDF,   filename: 'assets.pdf'   },
 *     { label: 'Excel', apiFn: exportAPI.assetsExcel, filename: 'assets.xlsx'  },
 *   ]} />
 */
export default function ExportMenu({ exports = [] }) {
  const [open, setOpen]         = useState(false);
  const ref                     = useRef(null);
  const { download, loading }   = useExport();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!exports.length) return null;

  // Single option — just render a button
  if (exports.length === 1) {
    const e = exports[0];
    return (
      <button
        onClick={() => download(e.apiFn, e.filename)}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
      >
        {loading ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
        {e.label}
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
      >
        {loading ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
        Export <ChevronDown size={13} className={clsx('transition-transform', open && 'rotate-180')}/>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
          {exports.map((e) => (
            <button
              key={e.filename}
              onClick={() => { setOpen(false); download(e.apiFn, e.filename); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-hover transition-colors text-left"
            >
              <Download size={13} className="text-gray-400"/> {e.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
